import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { UseMutateFunction } from '@tanstack/react-query';

import { TAssetProfile } from '@interfaces/assets/asset';
import { IDefaultValuesAttribute } from '@interfaces/components/form';

import { EValidationMode } from '@enums/validationMode';

import {
	assetStatusOptions,
	backhaulStatusOptions,
	booleanOptions,
	EUniqEditModeKeysExists,
	EUniqEditModeKeysStatuses,
	powerStatusOptions,
} from '@components/ModalComponents/AssetForm/constants';

import { AttributeCardBoolStatusHOC, AttributeCardEnumStatusHOC } from './AttributeCard';
import { schema } from './schema';

import styles from './Attributes.module.scss';

interface IAttributesProps {
	profile?: TAssetProfile;
	handleUpdateBackhaulAccessibility: UseMutateFunction<any>;
	handleUpdatePowerSupplyAvailability: UseMutateFunction<any>;
	handleUpdateAssetSiteAvailability: UseMutateFunction<any>;
	handleUpdateAssetAccessAvailability: UseMutateFunction<any>;
	handleUpdateAssetSpecialImplementationIssues: UseMutateFunction<any>;
	isEditAccess: boolean;
}

const Attributes = ({
	profile,
	handleUpdateBackhaulAccessibility,
	handleUpdatePowerSupplyAvailability,
	handleUpdateAssetSiteAvailability,
	handleUpdateAssetAccessAvailability,
	handleUpdateAssetSpecialImplementationIssues,
	isEditAccess,
}: IAttributesProps) => {
	const [isEditBackhaulAccessibility, setIsEditBackhaulAccessibility] = useState(false);
	const [isEditPowerSupplyAvailability, setIsEditPowerSupplyAvailability] = useState(false);
	const [isEditSiteAvailability, setIsEditSiteAvailability] = useState(false);

	const [isEditAssetAccessAvailability, setIsEditAssetAccessAvailability] = useState(false);
	const [isEditAssetSpecialImplementation, setIsEditAssetSpecialImplementation] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = useForm<IDefaultValuesAttribute>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			backhaulAccessibilityStatus: profile?.backhaulAccessibilityStatus?.name,
			backhaulAccessibilityDetails: profile?.backhaulAccessibilityDetails || null,
			powerSupplyAvailabilityStatus: profile?.powerSupplyAvailabilityStatus?.name,
			powerSupplyAvailabilityDetails: profile?.powerSupplyAvailabilityDetails || null,
			assetSiteAvailabilityStatus: profile?.assetSiteAvailabilityStatus?.name,
			assetSiteAvailabilityDetails: profile?.assetSiteAvailabilityDetails || null,
			isAvailable: profile?.isAvailable,
			assetAccessDetails: profile?.assetAccessDetails || null,
			specialImplementationIssuesExist: profile?.specialImplementationIssuesExist,
			specialImplementationIssueDetails: profile?.specialImplementationIssueDetails || null,
			assetAccessDetailsFileIds: profile?.assetAccessDetailsFileIds || [],
			backhaulAccessibilityDetailsFileIds: profile?.backhaulAccessibilityDetailsFileIds || [],
			assetSiteAvailabilityDetailsFileIds: profile?.assetSiteAvailabilityDetailsFileIds || [],
			powerSupplyAvailabilityDetailsFileIds: profile?.powerSupplyAvailabilityDetailsFileIds || [],
			specialImplementationIssueDetailsFileIds: profile?.specialImplementationIssueDetailsFileIds || [],
		},
	});

	const statusCards = [
		{
			title: 'Backhaul accessibility status',
			options: backhaulStatusOptions,
			editHandler: handleUpdateBackhaulAccessibility,
			isEditMode: isEditBackhaulAccessibility,
			uniqKey: EUniqEditModeKeysStatuses.uniqKeyBackhaulAccessibility,
			changeEditMode: setIsEditBackhaulAccessibility,
			statusName: 'backhaulAccessibilityStatus',
			detailsName: 'backhaulAccessibilityDetails',
			fileFieldName: 'backhaulAccessibilityDetailsFileIds',
		},
		{
			title: 'Power supply availability status',
			options: powerStatusOptions,
			editHandler: handleUpdatePowerSupplyAvailability,
			isEditMode: isEditPowerSupplyAvailability,
			uniqKey: EUniqEditModeKeysStatuses.uniqKeyPowerSupplyAvailability,
			changeEditMode: setIsEditPowerSupplyAvailability,
			statusName: 'powerSupplyAvailabilityStatus',
			detailsName: 'powerSupplyAvailabilityDetails',
			fileFieldName: 'powerSupplyAvailabilityDetailsFileIds',
		},
		{
			title: 'Asset site availability status',
			options: assetStatusOptions,
			editHandler: handleUpdateAssetSiteAvailability,
			isEditMode: isEditSiteAvailability,
			uniqKey: EUniqEditModeKeysStatuses.uniqKeySiteAvailability,
			changeEditMode: setIsEditSiteAvailability,
			statusName: 'assetSiteAvailabilityStatus',
			detailsName: 'assetSiteAvailabilityDetails',
			fileFieldName: 'assetSiteAvailabilityDetailsFileIds',
		},
	];

	const existCards = [
		{
			title: 'Asset site access availability',
			options: booleanOptions,
			editHandler: handleUpdateAssetAccessAvailability,
			isEditMode: isEditAssetAccessAvailability,
			changeEditMode: setIsEditAssetAccessAvailability,
			uniqKey: EUniqEditModeKeysExists.uniqKeyAssetAccessAvailability,
			statusName: 'isAvailable',
			detailsName: 'assetAccessDetails',
			fileFieldName: 'assetAccessDetailsFileIds',
		},
		{
			title: 'Special implementation issues exist',
			options: booleanOptions,
			editHandler: handleUpdateAssetSpecialImplementationIssues,
			isEditMode: isEditAssetSpecialImplementation,
			changeEditMode: setIsEditAssetSpecialImplementation,
			uniqKey: EUniqEditModeKeysExists.uniqKeyAssetSpecialImplementation,
			statusName: 'specialImplementationIssuesExist',
			detailsName: 'specialImplementationIssueDetails',
			fileFieldName: 'specialImplementationIssueDetailsFileIds',
		},
	];

	const changeStatus = (fieldName: string, value: any) => setValue(fieldName as any, value);

	const changeFiles = (fileFieldName: string, filesIds: Array<string>) => {
		setValue(fileFieldName as any, filesIds);
	};

	return (
		<>
			<div className={styles.attributesContainer}>
				<div>
					{statusCards.map(({
						title,
						options,
						editHandler,
						isEditMode,
						changeEditMode,
						statusName,
						detailsName,
						uniqKey,
						fileFieldName,
					}) => {

						return (
							<AttributeCardEnumStatusHOC
								key={uniqKey}
								changeEditMode={changeEditMode}
								changeFiles={changeFiles}
								changeStatus={changeStatus}
								detailsName={detailsName}
								editHandler={editHandler}
								errors={errors}
								fileFieldName={fileFieldName}
								handleSubmit={handleSubmit}
								id={profile?.id}
								isEditAccess={isEditAccess}
								isEditMode={isEditMode}
								options={options}
								register={register}
								statusName={statusName}
								title={title}
								watch={watch}
							/>
						);
					})}
				</div>
				<div>
					{existCards.map(({
						title,
						options,
						editHandler,
						isEditMode,
						changeEditMode,
						statusName,
						detailsName,
						uniqKey,
						fileFieldName,
					}) => {

						return (
							<AttributeCardBoolStatusHOC
								key={uniqKey}
								changeEditMode={changeEditMode}
								changeFiles={changeFiles}
								changeStatus={changeStatus}
								detailsName={detailsName}
								editHandler={editHandler}
								errors={errors}
								fileFieldName={fileFieldName}
								handleSubmit={handleSubmit}
								id={profile?.id}
								isEditAccess={isEditAccess}
								isEditMode={isEditMode}
								options={options}
								register={register}
								statusName={statusName}
								title={title}
								watch={watch}
							/>
						);
					})}
				</div>
			</div>
		</>
	);
};

export default Attributes;
